import { Container, Sprite, Texture } from 'pixi.js';
import gsap from 'gsap';
import type { Side } from '../config/tuning';

export type HeadState = 'idle' | 'inhale' | 'exhale' | 'choke' | 'warning' | 'win' | 'lose';
export type ArmState = 'idle' | 'holding' | 'passing';

const makeCentered = (key: string, x: number, y: number): Sprite => {
  const s = new Sprite(Texture.from(key));
  s.anchor.set(0.5);
  s.position.set(x, y);
  return s;
};

/**
 * Character — composite sprite (body + chest + arms + head + eye overlay). State
 * changes flip visibility on sub-sprites rather than swapping textures. Uses GSAP
 * for idle animations (chest breath) and reaction tweens (celebrate, slump, cough-shake).
 */
export class Character extends Container {
  readonly side: Side;
  private bodySprite: Sprite;
  private chest: Sprite;
  private offArm: Sprite;
  private armIdle: Sprite;
  private armHolding: Sprite;
  private armPassing: Sprite;
  private heads: Record<HeadState, Sprite>;
  private eyesOpen: Sprite;
  private eyesClosed: Sprite;
  private currentHead: HeadState = 'idle';
  private currentArm: ArmState = 'idle';
  private blinkInterval: number | null = null;

  constructor(side: Side, x: number, y: number, skin?: string) {
    super();
    this.side = side;
    this.position.set(x, y);
    this.sortableChildren = true;

    // `side` controls WHICH side of the screen the character is on and which
    // arm does the holding (left-hand for AI, right-hand for player). `skin`
    // controls the ART — asset-key prefix for all sub-sprites. Defaults to
    // side so callers that don't care about skins keep working unchanged.
    const p = skin ?? side;

    this.bodySprite = makeCentered(`${p}_body`, 0, 60);
    this.chest = makeCentered(`${p}_chest`, 0, 0);
    this.chest.alpha = 0.5;
    this.offArm = makeCentered(
      `${p}_arm_${side === 'player' ? 'left' : 'right'}_idle`,
      side === 'player' ? -90 : 90,
      60
    );

    const heldKey = side === 'player' ? 'right' : 'left';
    this.armIdle = makeCentered(`${p}_arm_${heldKey}_idle`, side === 'player' ? 90 : -90, 60);
    this.armHolding = makeCentered(`${p}_arm_${heldKey}_holding`, side === 'player' ? 70 : -70, -10);
    this.armPassing = makeCentered(`${p}_arm_${heldKey}_passing`, side === 'player' ? 110 : -110, -30);
    this.armHolding.visible = false;
    this.armPassing.visible = false;

    this.heads = {
      idle: makeCentered(`${p}_head_idle`, 0, -160),
      inhale: makeCentered(`${p}_head_inhale`, 0, -160),
      exhale: makeCentered(`${p}_head_exhale`, 0, -160),
      choke: makeCentered(`${p}_head_choke`, 0, -160),
      warning: makeCentered(`${p}_head_warning`, 0, -160),
      win: makeCentered(`${p}_head_win`, 0, -160),
      lose: makeCentered(`${p}_head_lose`, 0, -160),
    };
    // All non-idle heads start hidden.
    (['inhale', 'exhale', 'choke', 'warning', 'win', 'lose'] as HeadState[]).forEach(
      (s) => (this.heads[s].visible = false)
    );

    // Eye overlays align to the head's baked-in eye y-coordinate:
    // head at container y=-160 (center), SVG eye y≈142/240 → container y=-142.
    // For the AI, the idle-head SVG has had its eyes removed and the eyesOpen
    // overlay supplies them so the blink can swap in eyesClosed cleanly. For
    // the player, both overlays are empty (sunglasses hide everything).
    this.eyesOpen = makeCentered(`${p}_eyes_open`, 0, -142);
    this.eyesClosed = makeCentered(`${p}_eyes_closed`, 0, -142);
    // eyesOpen is the default face for idle; eyesClosed only shows during blink.
    this.eyesOpen.visible = true;
    this.eyesClosed.visible = false;

    this.addChild(
      this.bodySprite,
      this.chest,
      this.offArm,
      this.armIdle,
      this.armHolding,
      this.armPassing,
      this.heads.idle,
      this.heads.inhale,
      this.heads.exhale,
      this.heads.choke,
      this.heads.warning,
      this.heads.win,
      this.heads.lose,
      this.eyesOpen,
      this.eyesClosed
    );

    this.startIdleAnimations();
  }

  getHead(): HeadState {
    return this.currentHead;
  }

  setHead(state: HeadState): void {
    if (this.currentHead === state) return;
    this.heads[this.currentHead].visible = false;
    this.heads[state].visible = true;
    this.currentHead = state;
    // Only the idle head relies on the eyes overlay. Every other head SVG
    // paints its own eyes (squint, half-lid, X, stars, sad) that don't match
    // the open-eyes overlay, so hide it.
    const showOverlay = state === 'idle';
    this.eyesOpen.visible = showOverlay;
    this.eyesClosed.visible = false;
  }

  setArm(state: ArmState): void {
    if (this.currentArm === state) return;
    this.armIdle.visible = state === 'idle';
    this.armHolding.visible = state === 'holding';
    this.armPassing.visible = state === 'passing';
    this.currentArm = state;
  }

  /** World position of the joint anchor for this character's current pose.
   * Coordinates are derived from the fingertip positions in each arm SVG
   * (see public/assets/svg/{side}/arm_right_{state}.svg). If you edit an arm
   * SVG, re-measure the grip and update the offsets below to match. */
  getJointAnchorWorld(): { x: number; y: number } {
    const sign = this.side === 'player' ? 1 : -1;
    if (this.currentArm === 'passing') {
      // arm_passing sprite centered at (sign*110, -30); fingertip at SVG (244,60)
      // relative to SVG center (130,70) → offset (+114, -10).
      return { x: this.x + sign * 224, y: this.y - 40 };
    }
    if (this.currentArm === 'holding') {
      // arm_holding sprite centered at (sign*70, -10); fingertip at SVG (185,88)
      // relative to SVG center (100,90) → offset (+85, -2).
      return { x: this.x + sign * 155, y: this.y - 12 };
    }
    // Idle: hand hangs at hip height (arm_idle SVG is 80×200, hand at bottom).
    return { x: this.x + sign * 90, y: this.y + 60 };
  }

  triggerCelebrate(): void {
    const baseY = this.y;
    gsap.to(this, {
      y: baseY - 24,
      duration: 0.22,
      yoyo: true,
      repeat: 7, // Phaser's repeat:3 with yoyo runs 8 half-cycles total
      ease: 'sine.out',
      onComplete: () => { this.y = baseY; },
    });
  }

  triggerSlump(): void {
    gsap.to(this, { y: this.y + 12, duration: 0.38, ease: 'sine.in' });
  }

  triggerCoughShake(): void {
    const baseX = this.x;
    gsap.to(this, {
      x: baseX + 8,
      duration: 0.06,
      yoyo: true,
      repeat: 13, // Phaser's repeat:6 yoyo → 13 half-cycles after first
      ease: 'sine.inOut',
      onComplete: () => { this.x = baseX; },
    });
  }

  destroyAnims(): void {
    if (this.blinkInterval !== null) {
      window.clearInterval(this.blinkInterval);
      this.blinkInterval = null;
    }
    gsap.killTweensOf(this);
    gsap.killTweensOf(this.chest);
  }

  private startIdleAnimations(): void {
    gsap.to(this.chest.scale, {
      y: 1.08,
      duration: 1.8,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });

    const scheduleBlink = (): void => {
      const delay = 2400 + Math.random() * 2000;
      this.blinkInterval = window.setTimeout(() => {
        if (this.currentHead === 'idle') {
          this.eyesOpen.visible = false;
          this.eyesClosed.visible = true;
          window.setTimeout(() => {
            if (this.currentHead !== 'idle') return;
            this.eyesClosed.visible = false;
            this.eyesOpen.visible = true;
          }, 140);
        }
        scheduleBlink();
      }, delay);
    };
    scheduleBlink();
  }
}
